export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  readonly message: string;
  readonly variant?: ToastVariant;
  /** Override dwell; error defaults to 8s (same auto-dismiss family as warning). */
  readonly duration?: number;
  /**
   * Stable id — enqueue with the same id replaces that toast (visible or pending).
   * Use with `toastApi.dismiss(id)` to clear one toast without touching others.
   */
  readonly id?: string;
}

export type ToastApiHandler = (options: ToastOptions) => void;
export type ToastDismissHandler = (id: string) => void;

let toastHandler: ToastApiHandler | null = null;
let dismissHandler: ToastDismissHandler | null = null;

export function registerToastHandler(
  handler: ToastApiHandler,
  dismiss?: ToastDismissHandler,
): void {
  toastHandler = handler;
  dismissHandler = dismiss ?? null;
}

export function unregisterToastHandler(): void {
  toastHandler = null;
  dismissHandler = null;
}

/** Spec dwell: success/info 4000ms; warning/error 8000ms (errors auto-dismiss; not sticky). */
export function defaultToastDurationMs(variant: ToastVariant): number {
  if (variant === 'error' || variant === 'warning') {
    return 8000;
  }
  return 4000;
}

function dismissToastById(id: string): void {
  dismissHandler?.(id);
}

/**
 * Programmatic toast — wired to ToastProvider in MFE-v3-A-P1-4.
 * Spec §12.9 Success/Warning/Error scripts: see docs/governance/FORM_SCRIPTS_12_9.md
 * (code SoT; device timing Blocked under G4 / DEVICE_DOD). Prefer toastApi for non-blocking
 * save feedback. Do not toast field or in-form API errors that already have FormErrorSummary,
 * FormField.invalid / FormField.errorText, or an auth banner — one channel per message.
 */
function toastApiFn(message: string, variant?: ToastVariant): void;
function toastApiFn(options: ToastOptions): void;
function toastApiFn(
  messageOrOptions: string | ToastOptions,
  variant: ToastVariant = 'info',
): void {
  const options: ToastOptions =
    typeof messageOrOptions === 'string'
      ? { message: messageOrOptions, variant }
      : messageOrOptions;

  toastHandler?.(options);
}

toastApiFn.dismiss = dismissToastById;

export const toastApi: typeof toastApiFn & {
  dismiss: (id: string) => void;
} = toastApiFn;
